import { Component, OnInit } from '@angular/core';
import { Customer } from '../../classes/customer';
import { CustomerService } from '../../services/customer';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { GeneralModalComponent } from '../../modals/general/general-modal/general-modal.component';
import { CustomerCreateModalComponent } from '../../modals/customer/customer-create-modal/customer-create-modal.component';
import { AuthService } from '../../services/auth';
import { User } from '../../classes/user';
import {
    AdvancedMatch,
    AdvancedSearchField,
    AdvancedSearchState,
    advancedStateSignature,
    applyAdvancedSearch,
    countActiveRules,
    createRule,
    emptyAdvancedState,
    normalizeAdvancedState
} from '../../utils/advancedSearch';
import { loadAdvancedState, saveAdvancedState } from '../../utils/filterStateStorage';
import { csvFileName, downloadCsv, formatCsvDate } from '../../utils/csvExport';
import { PresetFilter } from '../preset-filters/preset-filters.component';
import { customerEmailAddresses, customerEmailsLabel as formatCustomerEmails } from '../../utils/customerEmails';

type CustomerColumn = 'id' | 'creationDate' | 'name' | 'vat' | 'email' | 'city' | 'mobile' | 'completed' | 'status';

interface CustomerPresetConfig {
    match: AdvancedMatch;
    rules: Array<{ field: string; operator: string; value?: string }>;
    columns: CustomerColumn[];
}

const DEFAULT_COLUMNS: CustomerColumn[] = ['id', 'creationDate', 'name', 'vat', 'email', 'city', 'mobile', 'completed', 'status'];

@Component({
    selector: 'app-customer',
    templateUrl: './customer.component.html',
    styleUrls: ['./customer.component.css']
})
export class CustomerComponent implements OnInit {
    // Pagination Parameters
    paginationConfig = {
        itemsPerPage: 25,
        currentPage: 1
    };
    columnLayoutToken = 0;
    paginationParams = {
        autoHide: true,
        maxSize: 7,
        responsive: true
    };
    paginationLabels = {
        previousLabel: 'Precedente',
        nextLabel: 'Successiva',
        screenReaderPaginationLabel: 'Paginazione',
        screenReaderPageLabel: 'pagina',
        screenReaderCurrentLabel: `Sei sulla pagina`
    };

    // Filters
    globalSearchFilterValue: string = '';
    idFilterValue: string = '';
    creationDateFromFilterValue: string = '';
    creationDateToFilterValue: string = '';
    companyNameFilterValue: string = '';
    vatNumberFilterValue: string = '';
    emailFilterValue: string = '';
    cityFilterValue: string = '';
    mobilePhoneFilterValue: string = '';
    enabledFilterValue: string[] = [];
    readonly enabledOptions: string[] = ['Attivo', 'Disabilitato'];

    // Filtri predefiniti e Ricerca Avanzata
    private readonly screenKey = 'clienti';
    readonly advancedFields: AdvancedSearchField<Customer>[] = [
        {key: 'id', label: 'ID Cliente', type: 'number', value: c => c.IDCustomer},
        {key: 'creationDate', label: 'Data creazione', type: 'date', value: c => c.CreationDate},
        {key: 'companyName', label: 'Ragione sociale', type: 'text', value: c => c.CompanyName},
        {key: 'legalForm', label: 'Forma giuridica', type: 'text', value: c => c.LegalForm},
        {key: 'vatNumber', label: 'Partita IVA', type: 'text', value: c => c.VATNumber},
        {key: 'fiscalCode', label: 'Codice Fiscale', type: 'text', value: c => c.FiscalCode},
        {key: 'atecoCode', label: 'Codice ATECO', type: 'text', value: c => c.AtecoCode},
        {key: 'chamberOfCommerce', label: 'Camera di Commercio', type: 'text', value: c => c.ChamberOfCommerce},
        {key: 'reaNumber', label: 'Numero REA', type: 'text', value: c => c.REANumber},
        {key: 'registrationDate', label: 'Data iscrizione', type: 'date', value: c => c.RegistrationDate},
        {key: 'visuraDate', label: 'Data visura', type: 'date', value: c => c.VisuraDate},
        {key: 'employees', label: 'Addetti', type: 'text', value: c => c.Employees},
        {key: 'email', label: 'Email', type: 'text', value: c => (c.emails || []).map(row => row.Email).filter(Boolean).join(', ')},
        {key: 'pec', label: 'Domicilio digitale (PEC)', type: 'text', value: c => c.PEC},
        {key: 'phone', label: 'Telefono fisso', type: 'text', value: c => c.Phone},
        {key: 'mobilePhone', label: 'Cellulare', type: 'text', value: c => c.MobilePhone},
        {key: 'headOffice', label: 'Sede legale (Comune)', type: 'text', value: c => c.HeadOffice},
        {key: 'headOfficeProvince', label: 'Provincia sede legale', type: 'text', value: c => c.HeadOfficeProvince},
        {key: 'address', label: 'Indirizzo sede legale', type: 'text', value: c => c.Address},
        {key: 'iban', label: 'IBAN', type: 'text', value: c => c.IBAN},
        {key: 'supportBankName', label: 'Banca di appoggio', type: 'text', value: c => c.SupportBankName},
        {key: 'supportBankBIC', label: 'BIC banca di appoggio', type: 'text', value: c => c.SupportBankBIC},
        {key: 'sdiCode', label: 'Codice SDI', type: 'text', value: c => c.SDICode},
        {key: 'ulaLastBalance', label: 'ULA ultimo bilancio', type: 'text', value: c => c.ULALastBalance},
        {key: 'salesRevenues', label: 'Ricavi vendite e prestazioni', type: 'text', value: c => c.SalesRevenues},
        {key: 'totalAssets', label: 'Totale attivo', type: 'text', value: c => c.TotalAssets},
        {key: 'balanceDate', label: 'Data bilancio', type: 'date', value: c => c.BalanceDate},
        {key: 'name', label: 'Nome legale rappr.', type: 'text', value: c => c.Name},
        {key: 'surname', label: 'Cognome legale rappr.', type: 'text', value: c => c.Surname},
        {key: 'birthPlace', label: 'Luogo nascita legale rappr.', type: 'text', value: c => c.BirthPlace},
        {key: 'birthDate', label: 'Data nascita legale rappr.', type: 'date', value: c => c.BirthDate},
        {key: 'city', label: 'Comune residenza legale rappr.', type: 'text', value: c => c.City},
        {key: 'province', label: 'Provincia residenza legale rappr.', type: 'text', value: c => c.Province},
        {
            key: 'enabled', label: 'Stato', type: 'select',
            options: [{value: 'true', label: 'Attivo'}, {value: 'false', label: 'Disabilitato'}],
            value: c => c.Enabled ? 'true' : 'false'
        },
        {key: 'requestsCount', label: 'Pratiche totali', type: 'number', value: c => Number(c.RequestsCount ?? 0)},
        {key: 'completedRequests', label: 'Pratiche completate', type: 'number', value: c => Number(c.CompletedRequestsCount ?? 0)}
    ];

    readonly presets: PresetFilter[] = [
        {id: 'tutti', label: 'Tutti', icon: 'fa-users', tone: 'neutral', description: 'Tutti i clienti, tutte le colonne'},
        {id: 'compilati', label: 'Compilati', icon: 'fa-check-circle', tone: 'success',
            description: 'Clienti con Ragione Sociale e Partita IVA compilate'},
        {id: 'da-compilare', label: 'Da Compilare', icon: 'fa-pencil-square-o', tone: 'warning',
            description: 'Clienti a cui manca la Ragione Sociale o la Partita IVA'},
        {id: 'consolidati', label: 'Consolidati', icon: 'fa-trophy', tone: 'primary',
            description: 'Clienti con almeno una pratica completata'}
    ];

    private readonly presetConfigs: { [id: string]: CustomerPresetConfig } = {
        'tutti': {match: 'and', rules: [], columns: DEFAULT_COLUMNS},
        'compilati': {
            match: 'and',
            rules: [
                {field: 'companyName', operator: 'notEmpty'},
                {field: 'vatNumber', operator: 'notEmpty'}
            ],
            columns: ['id', 'name', 'vat', 'email', 'city', 'mobile', 'completed', 'status']
        },
        'da-compilare': {
            match: 'or',
            rules: [
                {field: 'companyName', operator: 'empty'},
                {field: 'vatNumber', operator: 'empty'}
            ],
            columns: ['id', 'creationDate', 'name', 'vat', 'email', 'mobile', 'completed', 'status']
        },
        'consolidati': {
            match: 'and',
            rules: [{field: 'completedRequests', operator: 'gte', value: '1'}],
            columns: ['id', 'name', 'vat', 'email', 'city', 'mobile', 'completed', 'status']
        }
    };

    private presetSignatures: { [signature: string]: string } = {};

    advancedState: AdvancedSearchState = emptyAdvancedState();
    activePreset: string | null = 'tutti';
    presetCounts: { [id: string]: number } = {};
    visibleColumns: Set<CustomerColumn> = new Set(DEFAULT_COLUMNS);

    user: User = new User();
    customers: Array<Customer> = [];

    constructor(private router: Router,
                private route: ActivatedRoute,
                private authService: AuthService,
                private modalService: NgbModal,
                private customerService: CustomerService) {
    }

    ngOnInit(): void {
        Object.keys(this.presetConfigs).forEach(id => {
            const signature = advancedStateSignature(this.buildPresetState(id), this.advancedFields);
            this.presetSignatures[signature] = id;
        });
        this.restoreFilterState();

        this.authService.isUserSignedIn()
            .subscribe({
                next: () => {
                    this.authService.getUser()
                        .subscribe({
                            next: (user: any) => {
                                this.user = user;
                                if (this.user.FKIDUserRole !== 1) {
                                    this.router.navigate(['/dashboard']);
                                    return;
                                }
                                this.loadCustomers();
                            },
                            error: () => {
                                this.router.navigate(['/login']);
                            }
                        });
                },
                error: () => {
                    this.router.navigate(['/login']);
                }
            });
    }

    private loadCustomers(): void {
        this.customerService.getAll()
            .subscribe({
                next: (customers: Array<Customer>) => {
                    this.customers = customers;
                    this.recomputePresetCounts();
                    this.relockColumns();
                },
                error: (err: HttpErrorResponse) => {
                    console.log(err);
                }
            });
    }

    pageChanged(event: any) {
        this.paginationConfig.currentPage = event;
        this.relockColumns();
    }

    private relockColumns(): void {
        this.columnLayoutToken++;
    }

    onFilterChange(): void {
        this.paginationConfig.currentPage = 1;
    }

    private resetBasicFilters(): void {
        this.globalSearchFilterValue = '';
        this.idFilterValue = '';
        this.creationDateFromFilterValue = '';
        this.creationDateToFilterValue = '';
        this.companyNameFilterValue = '';
        this.vatNumberFilterValue = '';
        this.emailFilterValue = '';
        this.cityFilterValue = '';
        this.mobilePhoneFilterValue = '';
        this.enabledFilterValue = [];
    }

    clearFilters(): void {
        this.resetBasicFilters();
        this.advancedState = emptyAdvancedState();
        this.activePreset = 'tutti';
        this.visibleColumns = new Set(DEFAULT_COLUMNS);
        this.paginationConfig.currentPage = 1;
        this.persistFilterState();
        this.relockColumns();
    }

    hasActiveFilters(): boolean {
        return !!(
            this.globalSearchFilterValue
            || this.idFilterValue
            || this.creationDateFromFilterValue
            || this.creationDateToFilterValue
            || this.companyNameFilterValue
            || this.vatNumberFilterValue
            || this.emailFilterValue
            || this.cityFilterValue
            || this.mobilePhoneFilterValue
            || this.enabledFilterValue.length > 0
            || countActiveRules(this.advancedState, this.advancedFields) > 0
        );
    }

    // ---- Filtri predefiniti e Ricerca Avanzata ----

    isColumnVisible(column: CustomerColumn): boolean {
        return this.visibleColumns.has(column);
    }

    /** Colonne visibili + "Azioni", sempre presente. */
    get visibleColumnCount(): number {
        return this.visibleColumns.size + 1;
    }

    /** Un preset imposta insieme colonne e condizioni; sostituisce i filtri correnti. */
    applyPreset(id: string): void {
        if (!this.presetConfigs[id]) {
            return;
        }
        this.resetBasicFilters();
        this.advancedState = this.buildPresetState(id);
        this.activePreset = id;
        this.visibleColumns = new Set(this.presetConfigs[id].columns);
        this.paginationConfig.currentPage = 1;
        this.persistFilterState();
        this.relockColumns();
    }

    onAdvancedStateChange(state: AdvancedSearchState): void {
        this.advancedState = state;
        const detected = this.detectPreset(state);
        if (detected !== this.activePreset) {
            this.activePreset = detected;
            // Le condizioni coincidono con un preset (o sono state azzerate): ne seguo le colonne.
            if (detected) {
                this.visibleColumns = new Set(this.presetConfigs[detected].columns);
                this.relockColumns();
            }
        }
        this.paginationConfig.currentPage = 1;
        this.persistFilterState();
    }

    private buildPresetState(id: string): AdvancedSearchState {
        const config = this.presetConfigs[id];
        return normalizeAdvancedState({
            match: config.match,
            rules: config.rules.map(rule => createRule(
                this.advancedFields.find(field => field.key === rule.field),
                {operator: rule.operator, value: rule.value ?? ''}
            ))
        }, this.advancedFields);
    }

    private detectPreset(state: AdvancedSearchState): string | null {
        const signature = advancedStateSignature(state, this.advancedFields);
        if (signature === '') {
            return 'tutti';
        }
        return this.presetSignatures[signature] ?? null;
    }

    private recomputePresetCounts(): void {
        const counts: { [id: string]: number } = {};
        this.presets.forEach(preset => {
            counts[preset.id] = preset.id === 'tutti'
                ? this.customers.length
                : applyAdvancedSearch(this.customers, this.advancedFields, this.buildPresetState(preset.id)).length;
        });
        this.presetCounts = counts;
    }

    /** Priorità: preset nell'URL (?filtro=...), poi Ricerca Avanzata salvata nella sessione. */
    private restoreFilterState(): void {
        const requested = this.route.snapshot.queryParamMap.get('filtro');
        if (requested && requested !== 'tutti' && this.presetConfigs[requested]) {
            this.applyPreset(requested);
            return;
        }
        const stored = loadAdvancedState(this.screenKey, this.advancedFields);
        if (stored) {
            this.advancedState = stored;
            this.activePreset = this.detectPreset(stored);
            this.visibleColumns = new Set(
                this.presetConfigs[this.activePreset ?? 'tutti'].columns
            );
        }
    }

    /** Salva i filtri nella sessione e tiene l'URL condivisibile (?filtro=compilati). */
    private persistFilterState(): void {
        saveAdvancedState(this.screenKey, this.advancedState, this.advancedFields);
        const filtro = this.activePreset && this.activePreset !== 'tutti' ? this.activePreset : null;
        if (this.route.snapshot.queryParamMap.get('filtro') !== filtro) {
            this.router.navigate([], {
                relativeTo: this.route,
                queryParams: {filtro},
                queryParamsHandling: 'merge',
                replaceUrl: true
            });
        }
    }

    exportCsv(customers: Array<Customer>): void {
        const personName = (c: Customer) => `${c.Name || ''} ${c.Surname || ''}`.trim();
        const columns: Array<{ key: CustomerColumn; label: string; value: (c: Customer) => string | number }> = [
            {key: 'id', label: 'ID Cliente', value: c => c.IDCustomer ?? ''},
            {key: 'creationDate', label: 'Data Creazione', value: c => formatCsvDate(c.CreationDate)},
            {key: 'name', label: 'Ragione Sociale / Nominativo', value: c => c.CompanyName?.trim() || personName(c)},
            {key: 'vat', label: 'Partita IVA', value: c => c.VATNumber || ''},
            {key: 'email', label: 'Email', value: c => (c.emails || []).map(row => row.Email).filter(Boolean).join(', ')},
            {key: 'city', label: 'Città', value: c => c.HeadOffice?.trim() || c.City || ''},
            {key: 'mobile', label: 'Cellulare', value: c => c.MobilePhone || ''},
            {key: 'completed', label: 'Pratiche completate', value: c => Number(c.CompletedRequestsCount ?? 0)},
            {key: 'status', label: 'Stato', value: c => c.Enabled ? 'Attivo' : 'Disabilitato'}
        ];
        const visible = columns.filter(column => this.isColumnVisible(column.key));
        downloadCsv(
            csvFileName('clienti'),
            visible.map(column => column.label),
            customers.map(customer => visible.map(column => column.value(customer)))
        );
    }

    isEnabledSelected(status: string): boolean {
        return this.enabledFilterValue.includes(status);
    }

    onEnabledToggle(status: string, event: Event): void {
        const checked = (event.target as HTMLInputElement).checked;
        const selected = new Set(this.enabledFilterValue);
        if (checked) {
            selected.add(status);
        } else {
            selected.delete(status);
        }
        this.enabledFilterValue = this.enabledOptions.filter(option => selected.has(option));
        this.onFilterChange();
    }

    getEnabledFilterLabel(): string {
        if (this.enabledFilterValue.length === 0) {
            return 'Tutti';
        }
        return this.enabledFilterValue.join(', ');
    }

    goToCustomerDetail(idCustomer: number | undefined) {
        this.router.navigate(['/clienti/' + idCustomer]);
    }

    customerEmailsLabel(customer: Customer): string {
        return formatCustomerEmails(customer);
    }

    customerEmailList(customer: Customer): string[] {
        return customerEmailAddresses(customer);
    }

    openCreateCustomerModal() {
        const modalRef = this.modalService.open(CustomerCreateModalComponent, { size: 'xl', centered: true });
        modalRef.result.then((customerBack: Customer) => {
            if (customerBack?.IDCustomer !== undefined) {
                this.loadCustomers();
            }
        }, () => {});
    }

    openToggleEnabledModal(customer: Customer) {
        const willEnable = !customer.Enabled;
        const modalRef = this.modalService.open(GeneralModalComponent);
        modalRef.componentInstance.title = willEnable ? 'Riattivazione Cliente' : 'Disabilitazione Cliente';
        modalRef.componentInstance.text = willEnable
            ? `Confermi la riattivazione del cliente "${customer.CompanyName}"?`
            : `Confermi la disabilitazione del cliente "${customer.CompanyName}"? Il cliente non verrà eliminato, ma solo nascosto dalle operazioni correnti.`;
        modalRef.result.then(() => {
            customer.Enabled = willEnable;
            this.customerService.update(customer)
                .subscribe({
                    next: () => {},
                    error: (err: HttpErrorResponse) => {
                        customer.Enabled = !willEnable;
                        console.log(err);
                    }
                });
        }, () => {});
    }

}
